/**
 * The visible time window an anchored (historical) chart scrolls to, expressed
 * as minutes from ET midnight. 240 = 4:00, 1200 = 20:00. Callers stay in ET
 * wall-clock terms; {@link computeAnchorWindow} owns the EST/EDT offset.
 *
 * Each bound may also carry a **day offset**: 0 (default) = the event/anchor
 * day, -1 = the prior trading session, etc. This lets a window span sessions
 * (the overnight gap, a two-day run). Absent offsets ⇒ 0 ⇒ single-day behavior,
 * byte-identical to the original resolver.
 */
export interface AnchorVisibleWindow {
  /** Visible-range start, minutes from ET midnight (e.g. 240 = 4:00 AM ET). */
  startMin: number
  /** Visible-range end, minutes from ET midnight (e.g. 1200 = 8:00 PM ET). */
  endMin: number
  /** Session offset for the start bound; 0 = event day (default), -1 = prior. */
  startDayOffset?: number
  /** Session offset for the end bound; 0 = event day (default). */
  endDayOffset?: number
}

/** Full extended-hours session (4:00 AM–8:00 PM ET) — the historical default. */
export const DEFAULT_ANCHOR_WINDOW: AnchorVisibleWindow = { startMin: 240, endMin: 1200 }

const DAY_MS = 86_400_000

/**
 * Calendar milliseconds the anchored initial fetch must reach back to have the
 * bars for a window that starts `-startDayOffset` sessions before the event day.
 *
 * `N × 1.6` covers the ~1.44 weekend-inflated calendar days per trading session
 * with slack; the flat `+3` days is holiday headroom — holiday clusters (a
 * Thanksgiving week) are additive, not proportional to N, so a purely
 * proportional factor under-fetches at the margins. Returns 0 for a single-day
 * window (offset 0), so the caller's `max(pageMs, lookback)` is unchanged there.
 */
export function anchorLookbackMs(startDayOffset: number): number {
  const n = Math.max(0, -startDayOffset)
  if (n === 0) return 0
  return (n * 1.6 + 3) * DAY_MS
}

/**
 * Calendar milliseconds the anchored fetch must reach *forward* of the event day
 * to include an outcome bound `endDayOffset` sessions ahead (D[2] = the day
 * after). Same `N × 1.6 + 3` shape as the backward lookback. 0 for an
 * event-day-or-earlier end, so the anchored fetch stays pinned at the event day
 * (its historical default) unless the window explicitly looks ahead.
 */
export function anchorForwardMs(endDayOffset: number): number {
  const n = Math.max(0, endDayOffset)
  if (n === 0) return 0
  return (n * 1.6 + 3) * DAY_MS
}

/**
 * ET UTC-offset in hours for an instant: EDT (4) from the second Sunday in
 * March to the first Sunday in November, else EST (5).
 */
function etOffsetHours(timestamp: number): number {
  const year = new Date(timestamp).getUTCFullYear()
  const marchSecondSun = new Date(
    Date.UTC(year, 2, 8 + ((7 - new Date(Date.UTC(year, 2, 8)).getUTCDay()) % 7)),
  )
  const novFirstSun = new Date(
    Date.UTC(year, 10, 1 + ((7 - new Date(Date.UTC(year, 10, 1)).getUTCDay()) % 7)),
  )
  const isDST = timestamp >= marchSecondSun.getTime() && timestamp < novFirstSun.getTime()
  return isDST ? 4 : 5
}

/**
 * The ET calendar date of an instant, as a `Date.UTC(y, m, d)` key. Shifts the
 * instant into ET wall-clock before taking the date, so the extended session's
 * late tail (20:00 ET = next-day 01:00 UTC in winter) groups under its own ET
 * date, not a phantom next day. This is the one correct way to bucket bars into
 * trading sessions.
 */
function etDateKey(timestamp: number): number {
  const et = new Date(timestamp - etOffsetHours(timestamp) * 3_600_000)
  return Date.UTC(et.getUTCFullYear(), et.getUTCMonth(), et.getUTCDate())
}

/**
 * Distinct ET session date-keys present in a bar list, ascending. Used to walk
 * back N **trading** sessions for a negative day offset (so weekends/holidays
 * are skipped for free). Bars need only a `timestamp`.
 */
export function deriveSessionDateKeys(bars: ReadonlyArray<{ timestamp: number }>): number[] {
  const keys = new Set<number>()
  for (const bar of bars) keys.add(etDateKey(bar.timestamp))
  return [...keys].sort((a, b) => a - b)
}

/** Largest index in an ascending array whose value is ≤ `key`, or -1. */
function lastIndexAtMost(sorted: number[], key: number): number {
  let lo = 0
  let hi = sorted.length - 1
  let ans = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (sorted[mid] <= key) {
      ans = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return ans
}

/**
 * Step `n` weekdays from a `Date.UTC(y,m,d)` key (weekend-skip only). Negative
 * `n` steps back, positive steps forward — the calendar fallback when the loaded
 * sessions don't reach the target offset in either direction.
 */
function shiftWeekdays(dateKey: number, n: number): number {
  const dir = n < 0 ? -1 : 1
  let remaining = Math.abs(n)
  let d = new Date(dateKey)
  while (remaining > 0) {
    d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + dir))
    const dow = d.getUTCDay()
    if (dow !== 0 && dow !== 6) remaining--
  }
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

/** ET calendar date `{year, month, day}` for a bound at `offset` sessions back. */
function boundDate(
  anchorTs: number,
  offset: number,
  sessionDates: number[] | undefined,
): { year: number; month: number; day: number; ts: number } {
  if (offset === 0) {
    // The anchor's ET calendar date, the same bucketing the offset path and
    // deriveSessionDateKeys use. This used to be the anchor's UTC date, which
    // agrees with ET for a midday or 23:59 UTC anchor (History's) but not for
    // an exchange-time end-of-day anchor: the backtester's 23:59:59 ET sits at
    // 04:59 UTC the NEXT morning, so every window framed a day with no bars
    // and the chart scrolled to its end (Chris, 2026-09-05).
    const d = new Date(etDateKey(anchorTs))
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth(),
      day: d.getUTCDate(),
      ts: anchorTs,
    }
  }

  const anchorKey = etDateKey(anchorTs)
  let targetKey: number | undefined
  if (sessionDates && sessionDates.length > 0) {
    const idx = lastIndexAtMost(sessionDates, anchorKey)
    const targetIdx = idx + offset // offset < 0 walks back, > 0 walks forward
    if (idx >= 0 && targetIdx >= 0 && targetIdx < sessionDates.length) {
      targetKey = sessionDates[targetIdx]
    }
  }
  // Fallback: weekend-skipping calendar step. Approximate (no holidays), but the
  // caller clamps to loaded data, so it degrades to "as far as we have".
  if (targetKey === undefined) targetKey = shiftWeekdays(anchorKey, offset)

  const t = new Date(targetKey)
  return {
    year: t.getUTCFullYear(),
    month: t.getUTCMonth(),
    day: t.getUTCDate(),
    // Noon-ish on the target date, for that date's own DST offset.
    ts: targetKey + 17 * 3_600_000,
  }
}

/**
 * Given a focus-date timestamp and a window, return the `[from, to]` UTC
 * millisecond range to make visible. Each bound resolves to its ET calendar
 * date (offset 0 = the anchor date; negative = that many trading sessions back,
 * from `sessionDates`), then the ET minute is applied for that date's DST.
 *
 * `sessionDates` (ascending distinct ET date-keys from the loaded bars — see
 * {@link deriveSessionDateKeys}) is only consulted for a nonzero offset; the
 * single-day path never needs it and stays a pure function of the timestamp.
 */
export function computeAnchorWindow(
  timestamp: number,
  window: AnchorVisibleWindow = DEFAULT_ANCHOR_WINDOW,
  sessionDates?: number[],
): { from: number; to: number } {
  const start = boundDate(timestamp, window.startDayOffset ?? 0, sessionDates)
  const end = boundDate(timestamp, window.endDayOffset ?? 0, sessionDates)

  // `Date.UTC` normalizes overflowing minutes/hours, so `hours = etOffset` plus
  // the ET minutes yields the correct UTC instant (and rolls into the next day
  // for a late window end, exactly as the hardcoded 20:00 did before).
  const from = Date.UTC(
    start.year,
    start.month,
    start.day,
    etOffsetHours(start.ts),
    window.startMin,
    0,
  )
  const to = Date.UTC(end.year, end.month, end.day, etOffsetHours(end.ts), window.endMin, 0)
  return { from, to }
}

/**
 * True if any bar timestamp falls within `[from, to]` (inclusive). Binary search
 * — `bars` must be ascending by timestamp, which the chart buffer always is.
 */
export function rangeHasBars(
  bars: ReadonlyArray<{ timestamp: number }>,
  from: number,
  to: number,
): boolean {
  if (bars.length === 0 || from > to) return false
  // First bar with ts >= from.
  let lo = 0
  let hi = bars.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (bars[mid].timestamp < from) lo = mid + 1
    else hi = mid
  }
  return bars[lo].timestamp >= from && bars[lo].timestamp <= to
}

/**
 * Resolve the visible `[from, to]` an anchored chart should zoom to, with a
 * data-aware fallback. Normally this is the requested window. But if the
 * requested window contains NO loaded bars — a pre-market window on a day whose
 * first trade is after the open, an outcome window not yet reloaded — showing it
 * verbatim makes the chart scroll to an empty time and park on the neighbouring
 * (prior) session. In that case fall back to the full event-day session so the
 * viewer stays on the event day; if even that is empty (the anchor day itself
 * isn't loaded), return the requested window and let the caller's own fallback
 * handle it.
 *
 * Byte-identical to `computeAnchorWindow` whenever the requested window has bars,
 * so the single-day happy path is unchanged.
 */
export function resolveAnchorVisibleRange(
  bars: ReadonlyArray<{ timestamp: number }>,
  timestamp: number,
  window: AnchorVisibleWindow = DEFAULT_ANCHOR_WINDOW,
  sessionDates?: number[],
): { from: number; to: number } {
  const requested = computeAnchorWindow(timestamp, window, sessionDates)
  if (rangeHasBars(bars, requested.from, requested.to)) return requested
  const eventDay = computeAnchorWindow(timestamp, DEFAULT_ANCHOR_WINDOW, sessionDates)
  if (rangeHasBars(bars, eventDay.from, eventDay.to)) return eventDay
  return requested
}

/** ET minute of day (0..1439) of an instant, rounded to the minute. */
function etMinuteOfDay(timestamp: number): number {
  const et = timestamp - etOffsetHours(timestamp) * 3_600_000
  const msIntoDay = ((et % DAY_MS) + DAY_MS) % DAY_MS
  return Math.round(msIntoDay / 60_000)
}

/** Signed count of weekdays stepping from `fromKey` to `toKey` (calendar fallback). */
function weekdaysBetween(fromKey: number, toKey: number): number {
  if (fromKey === toKey) return 0
  const dir = toKey > fromKey ? 1 : -1
  let key = fromKey
  let steps = 0
  // Bounded: a window never spans more than a few sessions.
  while (key !== toKey && steps < 400) {
    key = shiftWeekdays(key, dir)
    steps += 1
  }
  return key === toKey ? steps * dir : steps * dir
}

/**
 * The inverse of {@link computeAnchorWindow}: express a visible `[from, to]`
 * range as a window relative to `anchorTs`, in ET minutes plus session offsets.
 * This is what lets one anchored chart's scroll or zoom be replayed on another
 * anchored chart that sits on a different day: "9:30 to 11:00 on the event day"
 * means the same thing on every cell.
 *
 * Session offsets come from the loaded bars when a bound falls on another
 * session (so weekends and holidays are skipped for free), with a weekday
 * count as the calendar fallback. Minutes are rounded, so an echo of a
 * received window round-trips to the same window and the group converges.
 */
export function windowFromRange(
  anchorTs: number,
  from: number,
  to: number,
  bars?: ReadonlyArray<{ timestamp: number }>,
): AnchorVisibleWindow {
  const anchorKey = etDateKey(anchorTs)
  let sessionDates: number[] | undefined
  const offsetOf = (key: number): number => {
    if (key === anchorKey) return 0
    if (bars && bars.length > 0) {
      sessionDates ??= deriveSessionDateKeys(bars)
      const a = lastIndexAtMost(sessionDates, anchorKey)
      const b = lastIndexAtMost(sessionDates, key)
      if (a >= 0 && b >= 0 && sessionDates[a] === anchorKey && sessionDates[b] === key) {
        return b - a
      }
    }
    return weekdaysBetween(anchorKey, key)
  }
  const startOffset = offsetOf(etDateKey(from))
  const endOffset = offsetOf(etDateKey(to))
  return {
    startMin: etMinuteOfDay(from),
    endMin: etMinuteOfDay(to),
    ...(startOffset !== 0 ? { startDayOffset: startOffset } : {}),
    ...(endOffset !== 0 ? { endDayOffset: endOffset } : {}),
  }
}
